import { useCallback, useEffect, useRef, useState } from "react";
import { Barcode, RefreshCw } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";

// UPC 码库 — one-way FileMaker (layout UPC_API) → App pull.
// Nightly cron 06:30 America/New_York + this manual trigger (admin only).
export function UpcSyncCard() {
  const { t } = useLanguage();
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  const timer = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/upc/sync/status");
      setStatus(data);
      if (data.state !== "running" && timer.current) {
        clearInterval(timer.current);
        timer.current = null;
      }
    } catch (error) {
      if (![401, 403].includes(error?.response?.status)) {
        console.warn("UPC sync status fetch failed:", error);
      }
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [fetchStatus]);

  useEffect(() => {
    if (status?.state === "running" && !timer.current) {
      timer.current = setInterval(fetchStatus, 3000);
    }
  }, [status, fetchStatus]);

  const start = async () => {
    setStarting(true);
    try {
      await api.post("/upc/sync");
      toast.success(t("upcSync.started"));
      fetchStatus();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setStarting(false); }
  };

  const running = status?.state === "running";

  return (
    <div className="bg-card border border-border rounded-md p-5 space-y-3" data-testid="upc-sync-card">
      <div className="flex items-center gap-2">
        <Barcode size={15} className="text-brand" />
        <h2 className="font-heading text-base font-semibold">{t("upcSync.title")}</h2>
      </div>
      <p className="text-xs text-muted-foreground">{t("upcSync.hint")}</p>
      <p className="text-[11px] font-mono text-muted-foreground" data-testid="upc-sync-status">
        {running
          ? `${t("upcSync.running")} ${status.processed}/${status.total || "?"}`
          : `${t("upcSync.stored")} ${status?.stored ?? 0}${status?.finished_at ? ` · ${t("upcSync.lastRun")} ${new Date(status.finished_at).toLocaleString()}` : ""}${status?.error ? ` · ${status.error}` : ""}`}
      </p>
      <Button variant="outline" size="sm" className="rounded-md" onClick={start}
        disabled={running || starting} data-testid="upc-sync-btn">
        <RefreshCw size={13} className={running ? "animate-spin" : ""} />
        {running ? t("upcSync.running") : t("upcSync.sync")}
      </Button>
    </div>
  );
}
